import React, { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { formatARS } from '../../utils/formatCurrency';

export interface CalendarCheque {
  id: number;
  partyType: 'supplier' | 'client';
  partyName: string;
  movementType: 'documento' | 'pago';
  amount: number;
  checkNumber: string;
  checkBank: string;
  checkIssueDate: string;
  checkDueDate: string;
  checkStatus: string;
}

export interface ChequeWeek {
  key: string;
  label: string;
  inflow: number;
  outflow: number;
  net: number;
  balance: number;
  cheques: CalendarCheque[];
}

const pad = (n: number) => String(n).padStart(2, '0');
const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shortDate = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
export const formatDMY = (iso: string) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : '-';
};

const mondayOf = (d: Date) => {
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
};

// Same rule as the backend cash flow: payment cheques that are not rejected and have not hit the bank yet.
export const isPendingCheque = (c: CalendarCheque, todayIso: string) => {
  if (c.movementType !== 'pago' || c.checkStatus === 'rechazado') return false;
  if (c.checkStatus === 'cobrado') return String(c.checkDueDate || '').slice(0, 10) > todayIso;
  return c.checkStatus === 'pendiente' || c.checkStatus === 'depositado' || !c.checkStatus;
};

export const chequeDirection = (c: CalendarCheque) => (c.partyType === 'client' ? 'Entra' : 'Sale');

export const buildChequeWeeks = (cheques: CalendarCheque[], startBalance: number, today = new Date()): ChequeWeek[] => {
  const todayIso = isoDate(today);
  const pending = cheques.filter((c) => isPendingCheque(c, todayIso));
  const rows: Array<Omit<ChequeWeek, 'net' | 'balance'>> = [];
  const add = (row: Omit<ChequeWeek, 'net' | 'balance'>) => { if (row.cheques.length) rows.push(row); };
  const sum = (list: CalendarCheque[], type: 'client' | 'supplier') => list.filter((c) => c.partyType === type).reduce((acc, c) => acc + Number(c.amount || 0), 0);
  const group = (key: string, label: string, list: CalendarCheque[]) => ({ key, label, inflow: sum(list, 'client'), outflow: sum(list, 'supplier'), cheques: list });

  const dated = pending.filter((c) => /^\d{4}-\d{2}-\d{2}/.test(String(c.checkDueDate || '')));
  const overdue = dated.filter((c) => c.checkDueDate.slice(0, 10) < todayIso);
  add(group('overdue', 'Vencidos, sin acreditar', overdue));

  const upcoming = dated.filter((c) => c.checkDueDate.slice(0, 10) >= todayIso).sort((a, b) => a.checkDueDate.localeCompare(b.checkDueDate));
  if (upcoming.length) {
    const last = upcoming[upcoming.length - 1].checkDueDate.slice(0, 10);
    const [ly, lm, ld] = last.split('-').map(Number);
    const lastMonday = mondayOf(new Date(ly, lm - 1, ld));
    for (let monday = mondayOf(today); monday <= lastMonday; monday.setDate(monday.getDate() + 7)) {
      const sunday = new Date(monday); sunday.setDate(sunday.getDate() + 6);
      const from = isoDate(monday); const to = isoDate(sunday);
      const list = upcoming.filter((c) => c.checkDueDate.slice(0, 10) >= from && c.checkDueDate.slice(0, 10) <= to);
      rows.push(group(from, `${shortDate(monday)} al ${shortDate(sunday)}${from <= todayIso && todayIso <= to ? ' (esta semana)' : ''}`, list));
    }
  }
  add(group('undated', 'Sin fecha de cobro', pending.filter((c) => !/^\d{4}-\d{2}-\d{2}/.test(String(c.checkDueDate || '')))));

  let balance = startBalance;
  return rows.map((row) => {
    balance += row.inflow - row.outflow;
    return { ...row, net: row.inflow - row.outflow, balance };
  });
};

export interface CoveragePiece { label: string; amount: number; }

export interface TimelineRow {
  cheque: CalendarCheque;
  date: string;
  overdue: boolean;
  balance: number;
  coveredBy: CoveragePiece[];
  usedFor: CoveragePiece[];
  shortfall: number;
}

export const chequeLabel = (c: CalendarCheque) => `Ch. ${c.checkNumber || 's/n'} ${c.partyName} (${formatDMY(c.checkDueDate).slice(0, 5)})`;
export const describePieces = (pieces: CoveragePiece[]) => pieces.map((p) => `${p.label}: ${formatARS(p.amount)}`).join(' + ');

// Día por día: cada pago a proveedor se cubre con el saldo en banco y los cheques de clientes
// disponibles hasta esa fecha, usando primero los más viejos. Si no alcanza queda un faltante
// que los cheques siguientes tapan antes de cubrir otros pagos, igual que el saldo acumulado.
export const buildChequeTimeline = (cheques: CalendarCheque[], startBalance: number, today = new Date()): TimelineRow[] => {
  const todayIso = isoDate(today);
  const rows: TimelineRow[] = cheques
    .filter((c) => isPendingCheque(c, todayIso) && /^\d{4}-\d{2}-\d{2}/.test(String(c.checkDueDate || '')))
    .map((c) => {
      const due = c.checkDueDate.slice(0, 10);
      return { cheque: c, date: due < todayIso ? todayIso : due, overdue: due < todayIso, balance: 0, coveredBy: [], usedFor: [], shortfall: 0 };
    })
    .sort((a, b) => a.date.localeCompare(b.date) || (a.cheque.partyType === b.cheque.partyType ? a.cheque.id - b.cheque.id : a.cheque.partyType === 'client' ? -1 : 1));
  const available: Array<{ label: string; remaining: number; row: TimelineRow | null }> = startBalance > 0 ? [{ label: 'Saldo en banco', remaining: startBalance, row: null }] : [];
  let deficit = startBalance < 0 ? -startBalance : 0;
  let balance = startBalance;
  for (const row of rows) {
    const amount = Number(row.cheque.amount || 0);
    if (row.cheque.partyType === 'client') {
      balance += amount;
      const repay = Math.min(deficit, amount);
      if (repay > 0) { deficit -= repay; row.usedFor.push({ label: 'Faltante anterior', amount: repay }); }
      if (amount - repay > 0.005) available.push({ label: chequeLabel(row.cheque), remaining: amount - repay, row });
    } else {
      balance -= amount;
      let need = amount;
      while (need > 0.005 && available.length) {
        const piece = available[0];
        const take = Math.min(piece.remaining, need);
        piece.remaining -= take; need -= take;
        row.coveredBy.push({ label: piece.label, amount: take });
        piece.row?.usedFor.push({ label: chequeLabel(row.cheque), amount: take });
        if (piece.remaining <= 0.005) available.shift();
      }
      if (need > 0.005) { row.shortfall = need; deficit += need; }
    }
    row.balance = balance;
  }
  return rows;
};

const BANK_BALANCE_KEY = 'polcen.chequeCalendar.bankBalance';
export const readSavedBankBalance = () => {
  try { return Number(localStorage.getItem(BANK_BALANCE_KEY) || 0) || 0; } catch { return 0; }
};

const ChequeCalendar: React.FC<{ cheques: CalendarCheque[]; bankBalance: number; onBankBalanceChange: (value: number) => void }> = ({ cheques, bankBalance, onBankBalanceChange }) => {
  const [open, setOpen] = useState<string | null>(null);
  const [hideEmpty, setHideEmpty] = useState(true);
  const weeks = useMemo(() => buildChequeWeeks(cheques, bankBalance), [cheques, bankBalance]);
  const visible = hideEmpty ? weeks.filter((w) => w.cheques.length) : weeks;
  const timeline = useMemo(() => buildChequeTimeline(cheques, bankBalance), [cheques, bankBalance]);
  const timelineById = useMemo(() => new Map(timeline.map((row) => [row.cheque.id, row])), [timeline]);
  const unfunded = timeline.filter((row) => row.shortfall > 0);
  const lowest = timeline.reduce<TimelineRow | null>((min, row) => (!min || row.balance < min.balance ? row : min), null);

  const setBalance = (value: number) => {
    onBankBalanceChange(value);
    try { localStorage.setItem(BANK_BALANCE_KEY, String(value)); } catch { /* sin almacenamiento: queda solo en esta sesión */ }
  };

  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Calendario de cheques</h2>
          <p className="mt-1 text-xs text-gray-600">Semana por semana: lo que entra por cheques de clientes, lo que sale por cheques a proveedores y cómo queda la plata acumulada.</p>
        </div>
        <label className="text-xs font-medium text-gray-600">Saldo actual en banco (opcional)
          <input type="number" step="0.01" value={bankBalance || ''} placeholder="0" onChange={(e) => setBalance(Number(e.target.value) || 0)} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 sm:w-56" />
        </label>
      </div>

      {unfunded.length > 0 ? (
        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p><strong>{unfunded.length} pago{unfunded.length === 1 ? '' : 's'} a proveedores sin fondos</strong> a la fecha en que se cobra{unfunded.length === 1 ? '' : 'n'}:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {unfunded.slice(0, 5).map((row) => <li key={row.cheque.id}>{formatDMY(row.date)} · {row.cheque.partyName} · Ch. {row.cheque.checkNumber || 's/n'} por {formatARS(row.cheque.amount)}: faltan <strong>{formatARS(row.shortfall)}</strong></li>)}
            </ul>
            {unfunded.length > 5 && <p className="mt-1 text-xs">Y {unfunded.length - 5} más; están marcados en rojo en cada semana.</p>}
            {lowest && lowest.balance < 0 && <p className="mt-1 text-xs">El punto más bajo es el {formatDMY(lowest.date)}, con {formatARS(lowest.balance)}.</p>}
          </div>
        </div>
      ) : timeline.length > 0 && (
        <p className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">Todos los pagos a proveedores quedan cubiertos por el saldo en banco y los cheques de clientes que entran antes o el mismo día.</p>
      )}

      {weeks.length === 0 ? <p className="text-sm text-gray-500">No hay cheques pendientes de cobro o de pago.</p> : <>
        <label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />Ocultar semanas sin cheques</label>
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr><th className="px-3 py-3 sm:px-4">Semana</th><th className="px-3 py-3 text-right sm:px-4">Entra</th><th className="px-3 py-3 text-right sm:px-4">Sale</th><th className="px-3 py-3 text-right sm:px-4">Neto</th><th className="px-3 py-3 text-right sm:px-4">Acumulado</th></tr>
            </thead>
            <tbody className="divide-y">
              <tr className="bg-gray-50/60 text-gray-600"><td className="px-3 py-2 sm:px-4" colSpan={4}>Saldo inicial</td><td className="px-3 py-2 text-right font-medium sm:px-4">{formatARS(bankBalance)}</td></tr>
              {visible.map((week) => (
                <React.Fragment key={week.key}>
                  <tr className={`${week.cheques.length ? 'cursor-pointer hover:bg-gray-50' : 'text-gray-400'} ${week.balance < 0 || week.cheques.some((c) => (timelineById.get(c.id)?.shortfall || 0) > 0) ? 'bg-red-50/60' : ''}`} onClick={() => week.cheques.length && setOpen(open === week.key ? null : week.key)}>
                    <td className="whitespace-nowrap px-3 py-2.5 font-medium sm:px-4">
                      <span className="inline-flex items-center gap-1">{week.cheques.length ? (open === week.key ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}{week.label}</span>
                      {week.cheques.length > 0 && <span className="ml-5 block text-xs font-normal text-gray-500">{week.cheques.length} cheque{week.cheques.length === 1 ? '' : 's'}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right text-green-700 sm:px-4">{week.inflow ? formatARS(week.inflow) : '-'}</td>
                    <td className="px-3 py-2.5 text-right text-red-600 sm:px-4">{week.outflow ? formatARS(week.outflow) : '-'}</td>
                    <td className={`px-3 py-2.5 text-right font-medium sm:px-4 ${week.net >= 0 ? 'text-blue-800' : 'text-red-700'}`}>{week.cheques.length ? formatARS(week.net) : '-'}</td>
                    <td className={`px-3 py-2.5 text-right font-bold sm:px-4 ${week.balance >= 0 ? 'text-gray-900' : 'text-red-700'}`}>{formatARS(week.balance)}</td>
                  </tr>
                  {open === week.key && (
                    <tr><td colSpan={5} className="bg-gray-50 px-3 py-2 sm:px-4">
                      <div className="space-y-1">
                        {week.cheques.map((c) => (
                          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded border bg-white px-3 py-2 text-xs">
                            <span><span className={`mr-2 rounded-full px-2 py-0.5 font-semibold ${c.partyType === 'client' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{chequeDirection(c)}</span><strong>{c.partyName}</strong> · N° {c.checkNumber || '-'} · {c.checkBank || '-'} · cobro {formatDMY(c.checkDueDate)}</span>
                            <span className={`font-semibold ${c.partyType === 'client' ? 'text-green-700' : 'text-red-600'}`}>{formatARS(c.amount)}</span>
                            {(() => {
                              const row = timelineById.get(c.id);
                              if (!row) return null;
                              if (c.partyType === 'client') return row.usedFor.length ? <p className="w-full text-gray-500">Se usa para: {describePieces(row.usedFor)}</p> : <p className="w-full text-gray-500">Queda libre, no cubre ningún pago.</p>;
                              return <p className={`w-full ${row.shortfall ? 'font-semibold text-red-700' : 'text-gray-500'}`}>{row.coveredBy.length ? `Cubierto con: ${describePieces(row.coveredBy)}` : 'Sin nada que lo cubra'}{row.shortfall ? ` · ⚠ Sin fondos: faltan ${formatARS(row.shortfall)}` : ''}</p>;
                            })()}
                          </div>
                        ))}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500">Solo cheques pendientes o depositados (los acreditados con fecha pasada ya están en el banco; los rechazados no cuentan). No incluye transferencias o efectivo futuros, gastos, impuestos ni sueldos. Tocá una semana para ver sus cheques y con qué se cubre cada pago. Los vencidos sin acreditar se toman como disponibles o a pagar hoy.</p>
      </>}
    </section>
  );
};

export default ChequeCalendar;
